import { useMutation, useQuery } from '@apollo/client';
import {
    faDatabase,
    faEllipsisVertical,
    faFolder,
    faGear,
    faPlug,
    faRobot,
    faPlus,
    faUser,
    faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { CardMenu, dangerItemClassName, itemClassName } from './automations/AutomationCard.tsx';
import { ProjectAgentBadge } from './ProjectAgentBadge.tsx';
import { ProjectCreateModal } from './ProjectCreateModal.tsx';
import {
    PROJECT_FORM_DEFAULTS,
    ProjectDetailsFields,
    ProjectFormValues,
    ProjectInstructionsFields,
    ProjectKnowledgeFields,
    toInstructionInputs,
    toKnowledgeSourceInputs,
    toToolConfigInputs,
    toToolConfigValues,
} from './ProjectFormFields.tsx';
import { ProjectSharingFields, displayName } from './ProjectSharingFields.tsx';
import { ProjectToolsFields } from './ProjectToolsFields.tsx';
import { splitRecipients, toRecipientValues } from './RecipientsField.tsx';
import { SidebarToggle } from './SidebarToggle.tsx';
import Alert from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import EmptyStage from '../../core/components/EmptyStage.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Loader from '../../core/components/Loader.tsx';
import Page from '../../core/components/Page';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import SecondaryNavigation from '../../core/components/SecondaryNavigation.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import { url } from '../../core/utils/link.ts';
import { ASSISTANT_PATH } from '../../products/assistants/constants.ts';
import AssistantCardShell from '../../products/assistants/components/AssistantCardShell.tsx';
import { isValidKnowledgeUrl } from '../../products/assistants/components/config/ConfigSteps.tsx';
import { canConfigureKnowledgeSources, canManageAgents } from '../../users/utils.ts';
import { CHAT_PATH, PROJECT_PATH, PROJECTS_PATH } from '../constants.ts';
import { graphqlErrorMessage } from '../errors.ts';
import {
    DELETE_PROJECT,
    DELETE_PROJECT_KNOWLEDGE_FILE,
    GET_PROJECT,
    GET_PROJECTS,
    SHARE_PROJECT,
    UPDATE_PROJECT,
    UPLOAD_PROJECT_KNOWLEDGE_FILE,
} from '../queries.ts';

const PlusIcon = withIcon(faPlus);
const ProjectIcon = withIcon(faFolder);
const DatabaseIcon = withIcon(faDatabase);
const OwnerIcon = withIcon(faUser);
const EllipsisIcon = withIcon(faEllipsisVertical);
const GearIcon = withIcon(faGear);
const ToolsIcon = withIcon(faPlug);
const UsersIcon = withIcon(faUsers);
const AgentIcon = withIcon(faRobot);

interface DeleteProjectConfirmProps {
    project: { name: string };
    fileCount: number;
    isDisabled: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/** Deleting a project removes its files for everyone it is shared with; the chats that used it stay. */
const DeleteProjectConfirm = ({ project, fileCount, isDisabled, onConfirm, onCancel }: DeleteProjectConfirmProps) => {
    const { t } = useTranslation('chat');
    return (
        <ConfirmModal
            title={t('projects.delete.title')}
            labelConfirm={t('projects.delete.yes')}
            confirmDisabled={isDisabled}
            danger
            onConfirm={onConfirm}
            onCancel={onCancel}
        >
            {[
                t('projects.delete.confirm', { name: project.name }),
                fileCount > 0 && t('projects.delete.confirmFiles', { count: fileCount }),
                t('projects.delete.confirmRest'),
            ]
                .filter(Boolean)
                .join(' ')}
        </ConfirmModal>
    );
};

const knowledgeFileCount = (project) => project.knowledgeFiles.filter((file) => file.sourceType !== 'url').length;

type Section = 'details' | 'knowledge' | 'tools' | 'sharing';
const SECTIONS: Section[] = ['details', 'knowledge', 'tools', 'sharing'];
const SECTION_ICONS = { details: GearIcon, knowledge: DatabaseIcon, tools: ToolsIcon, sharing: UsersIcon } as const;
const isSection = (value: string | undefined): value is Section => SECTIONS.includes(value as Section);

const shareLabel = (t, project) => {
    if (project.team) return t('projects.sharedTeam', { team: project.team.name });
    if (project.members?.length) return t('projects.sharedPeople', { count: project.members.length });
    return t('projects.onlyYou');
};

/** One hub card per project, the same frame as the assistant cards: the card opens the project, the hover menu
 *  holds the actions. Every project gets the folder tile; a project an inbox agent uses says so in a meta line. */
const ProjectCard = ({ project }) => {
    const { t } = useTranslation('chat');
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState(false);
    const [deleteProject, { loading: deleteLoading }] = useMutation(DELETE_PROJECT, {
        refetchQueries: [{ query: GET_PROJECTS }],
    });

    const mine = project.owner?.id === user?.id;
    // an inbox agent's project is configured on the agent, so it has no manage actions here
    const canManage = Boolean(project.canManage) && !project.agent;
    const detailPath = url(PROJECT_PATH, { projectId: project.id });

    const handleDelete = async () => {
        try {
            await deleteProject({ variables: { id: project.id } });
            publishToast({ description: t('projects.delete.done'), status: 'success' });
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('projects.saveError')), status: 'error' });
        }
        setDeleting(false);
    };

    const fileCount = knowledgeFileCount(project);
    const knowledgeParts = [
        project.sharepointKnowledgeSource && t('projects.card.sharepoint'),
        project.sftpKnowledgeSource && t('projects.card.sftp'),
        project.knowledgeUrls?.length > 0 && t('projects.card.webPages', { count: project.knowledgeUrls.length }),
        fileCount > 0 && t('projects.filesCount', { count: fileCount }),
    ].filter(Boolean);
    const knowledge = knowledgeParts.length > 0 ? knowledgeParts.join(' · ') : t('projects.card.noKnowledge');

    const meta = [
        { icon: <DatabaseIcon />, label: knowledge },
        ...(project.agent
            ? [{ icon: <AgentIcon />, label: t('projects.card.usedBy', { name: project.agent.name }) }]
            : !mine && project.owner
              ? [{ icon: <OwnerIcon />, label: t('projects.byOwner', { name: displayName(project.owner) }) }]
              : []),
    ];

    // the same hover ⋮ menu as the automation cards; it keeps its clicks away from the card link
    const addon = (
        <CardMenu isDisabled={deleteLoading} label={t('projects.card.actions')}>
            {canManage && (
                <DropdownMenu.Item className={itemClassName} onClick={() => navigate(detailPath)}>
                    {t('projects.card.edit')}
                </DropdownMenu.Item>
            )}
            <DropdownMenu.Item
                className={itemClassName}
                onClick={() => navigate(CHAT_PATH, { state: { projectId: project.id } })}
            >
                {t('projects.newConversation')}
            </DropdownMenu.Item>
            {project.agent && canManageAgents(user) && (
                <DropdownMenu.Item
                    className={itemClassName}
                    onClick={() => navigate(url(ASSISTANT_PATH, { channelId: project.agent.id, tab: 'knowledge' }))}
                >
                    {t('projects.card.editInAssistants')}
                </DropdownMenu.Item>
            )}
            {canManage && (
                <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item className={dangerItemClassName} onClick={() => setDeleting(true)}>
                        {t('projects.delete.action')}
                    </DropdownMenu.Item>
                </>
            )}
        </CardMenu>
    );

    return (
        <>
            <AssistantCardShell
                to={detailPath}
                icon={<ProjectIcon />}
                title={project.name}
                subtitle={shareLabel(t, project)}
                addon={addon}
                meta={meta}
            />
            {deleting && (
                <DeleteProjectConfirm
                    project={project}
                    fileCount={fileCount}
                    isDisabled={deleteLoading}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleting(false)}
                />
            )}
        </>
    );
};

const ProjectList = ({ onOpenSidebar }) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const location = useLocation();

    const { data, loading, refetch } = useQuery(GET_PROJECTS, {
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('projects.loadError')), status: 'error' }),
    });
    const projects = data?.projects || [];

    // the composer's "New project…" lands here with the modal already open
    const [creating, setCreating] = useState(Boolean((location.state as { createProject?: boolean })?.createProject));
    useEffect(() => {
        if ((location.state as { createProject?: boolean })?.createProject)
            navigate(PROJECTS_PATH, { replace: true, state: {} });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <Page.Header className="justify-between">
                <Page.HeaderTitle>{t('projects.title')}</Page.HeaderTitle>
                <div className="flex items-center gap-2">
                    {projects.length > 0 && (
                        <Page.HeaderButton type="button" onClick={() => setCreating(true)}>
                            <PlusIcon />
                            {t('projects.new')}
                        </Page.HeaderButton>
                    )}
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </Page.Header>
            <Page.Content lowered className="flex flex-col gap-4">
                {loading && !projects.length ? (
                    <Loader />
                ) : projects.length === 0 ? (
                    <EmptyStage
                        title={t('projects.emptyTitle')}
                        description={t('projects.empty')}
                        action={
                            <Button variant="primary" onClick={() => setCreating(true)}>
                                <PlusIcon className="mr-1.5" />
                                {t('projects.new')}
                            </Button>
                        }
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {projects.map((project) => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                )}
            </Page.Content>
            {creating && (
                <ProjectCreateModal
                    onClose={() => setCreating(false)}
                    onCreated={async (project) => {
                        setCreating(false);
                        await refetch();
                        navigate(url(PROJECT_PATH, { projectId: project.id }));
                    }}
                />
            )}
        </div>
    );
};

const ProjectDetail = ({ projectId, sectionParam, onOpenSidebar }) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { user } = useApplicationContext();
    const section: Section = isSection(sectionParam) ? sectionParam : 'details';
    const [saving, setSaving] = useState(false);

    const { data, loading, refetch } = useQuery(GET_PROJECT, {
        variables: { id: projectId },
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('projects.loadError')), status: 'error' }),
    });
    const project = data?.project;

    const [deleting, setDeleting] = useState(false);
    const [deleteProject, { loading: deleteLoading }] = useMutation(DELETE_PROJECT, {
        refetchQueries: [{ query: GET_PROJECTS }],
    });
    const handleDelete = async () => {
        try {
            await deleteProject({ variables: { id: project.id } });
            publishToast({ description: t('projects.delete.done'), status: 'success' });
            navigate(url(PROJECTS_PATH));
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('projects.saveError')), status: 'error' });
            setDeleting(false);
        }
    };

    // an inbox agent's project is configured on the agent; here it is read-only even for admins
    const canManage = Boolean(project?.canManage) && !project?.agent;
    // any member may add knowledge, except on agent projects (managed on the agent's knowledge tab)
    const canEditKnowledge = Boolean(project) && !project.agent;
    // synced sources, ERP and order history point at company systems: managers who may also configure agents or
    // integrations set them up
    const canConfigureSources = canManage && canConfigureKnowledgeSources(user);

    const form = useForm<ProjectFormValues>({ defaultValues: PROJECT_FORM_DEFAULTS, mode: 'onTouched' });
    useEffect(() => {
        if (!project) return;
        form.reset({
            name: project.name,
            description: project.description || '',
            instructions: project.instructions || [],
            // URL-synced files are represented by their URL rows, not as file cards (same as the agent config)
            files: project.knowledgeFiles.filter((file) => file.sourceType !== 'url'),
            sharepointKnowledgeSource: project.sharepointKnowledgeSource,
            sftpKnowledgeSource: project.sftpKnowledgeSource,
            knowledgeUrls: project.knowledgeUrls || [],
            ...toToolConfigValues(project),
            recipients: toRecipientValues(project.team, project.members),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]);

    const [updateProject] = useMutation(UPDATE_PROJECT);
    const [shareProject] = useMutation(SHARE_PROJECT);
    const [uploadFile] = useMutation(UPLOAD_PROJECT_KNOWLEDGE_FILE);
    const [deleteFile] = useMutation(DELETE_PROJECT_KNOWLEDGE_FILE);

    const recipients = form.watch('recipients');
    const urlsValid = (form.watch('knowledgeUrls') || []).every(
        (url: string) => !url.trim() || isValidKnowledgeUrl(url)
    );

    const handleSubmit = async (values: ProjectFormValues) => {
        setSaving(true);
        const failed: string[] = [];
        try {
            if (section === 'knowledge') {
                // same delta as the agent config: delete the removed rows, upload the newly dropped files
                const current = values.files || [];
                const removed = project.knowledgeFiles.filter(
                    (file) => !file.isSynced && !current.some((candidate) => candidate.id === file.id)
                );
                const added = current.filter((candidate): candidate is File => candidate instanceof File);
                for (const file of removed) {
                    try {
                        await deleteFile({ variables: { id: file.id } });
                    } catch {
                        failed.push(file.fileName);
                    }
                }
                for (const file of added) {
                    try {
                        await uploadFile({ variables: { projectId: project.id, file } });
                    } catch {
                        failed.push(file.name);
                    }
                }
                // members only add files; the sources belong to the manager's save
                if (canManage) {
                    await updateProject({
                        variables: {
                            id: project.id,
                            instructions: toInstructionInputs(values.instructions),
                            ...toKnowledgeSourceInputs(values, { includeSynced: canConfigureSources }),
                        },
                    });
                }
            } else if (section === 'tools') {
                await updateProject({
                    variables: {
                        id: project.id,
                        ...toToolConfigInputs(values, { includeProviders: canConfigureSources }),
                    },
                });
            } else if (section === 'sharing') {
                await shareProject({ variables: { id: project.id, ...splitRecipients(values.recipients) } });
            } else {
                await updateProject({
                    variables: {
                        id: project.id,
                        name: values.name.trim(),
                        description: values.description.trim(),
                        instructions: toInstructionInputs(values.instructions),
                    },
                });
            }
            await refetch();
            if (failed.length > 0) {
                publishToast({
                    description: t('projects.knowledge.partialError', { count: failed.length }),
                    status: 'warning',
                });
            } else {
                publishToast({ description: t('projects.form.saved'), status: 'success' });
            }
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('projects.saveError')), status: 'error' });
            await refetch();
        } finally {
            setSaving(false);
        }
    };

    if (loading && !project) return <Loader />;
    if (!project) return <div className="p-8 text-sm text-content-medium">{t('projects.notFound')}</div>;

    const editable = section === 'knowledge' ? canEditKnowledge : canManage;
    const ownerName = project.owner ? displayName(project.owner) : t('projects.admin');

    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <Page.Header className="flex items-center">
                <Page.HeaderBreadcrumb to={url(PROJECTS_PATH)}>{t('projects.title')}</Page.HeaderBreadcrumb>
                <Page.HeaderTitle className="flex min-w-0 items-center gap-2 truncate">
                    <ProjectIcon className="flex-none text-content-medium" />
                    {project.name}
                </Page.HeaderTitle>
                <ProjectAgentBadge project={project} className="ml-3" />
                <div className="ml-auto flex items-center gap-2">
                    <Page.HeaderButton
                        type="button"
                        onClick={() => navigate(CHAT_PATH, { state: { projectId: project.id } })}
                    >
                        <PlusIcon />
                        {t('projects.newConversation')}
                    </Page.HeaderButton>
                    {canManage && (
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Page.HeaderIconButton
                                    type="button"
                                    variant="ghost"
                                    aria-label={t('projects.card.actions')}
                                    disabled={deleteLoading}
                                >
                                    <EllipsisIcon />
                                </Page.HeaderIconButton>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align="end" className="min-w-48">
                                <DropdownMenu.Item className={dangerItemClassName} onClick={() => setDeleting(true)}>
                                    {t('projects.delete.action')}
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    )}
                    <SidebarToggle onClick={onOpenSidebar} />
                </div>
            </Page.Header>

            <div className="flex min-h-0 flex-1 flex-col md:flex-row">
                <SecondaryNavigation>
                    <SecondaryNavigation.List>
                        {SECTIONS.map((key) => {
                            const SectionIcon = SECTION_ICONS[key];
                            return (
                                <SecondaryNavigation.Button key={key} asChild active={section === key}>
                                    <Link to={url(PROJECT_PATH, { projectId: project.id, section: key })}>
                                        <SectionIcon className="flex-none" />
                                        {t(`projects.sections.${key}`)}
                                    </Link>
                                </SecondaryNavigation.Button>
                            );
                        })}
                    </SecondaryNavigation.List>
                </SecondaryNavigation>

                <Page.Content lowered>
                    <div className="flex max-w-3xl flex-col gap-4">
                        {project.agent ? (
                            <Alert severity="info">
                                <span>
                                    {t('projects.readOnlyAgent')}
                                    {canManageAgents(user) && (
                                        <>
                                            {' '}
                                            <Link
                                                className="font-medium text-content-brand-default hover:underline"
                                                to={url(ASSISTANT_PATH, {
                                                    channelId: project.agent.id,
                                                    tab: 'knowledge',
                                                })}
                                            >
                                                {t('projects.openAgent')}
                                            </Link>
                                        </>
                                    )}
                                </span>
                            </Alert>
                        ) : (
                            !project.canManage && (
                                <Alert severity="info">{t('projects.readOnlyShared', { name: ownerName })}</Alert>
                            )
                        )}

                        <SettingsFormCard
                            key={section}
                            onSubmit={form.handleSubmit(handleSubmit)}
                            title={t(`projects.${section}.title`)}
                            description={t(`projects.${section}.description`)}
                            footer={
                                editable && (
                                    <SaveStatus
                                        isDirty={form.formState.isDirty}
                                        isSubmitting={saving}
                                        errorCount={countFieldErrors(form.formState.errors)}
                                        isDisabled={section === 'knowledge' && !urlsValid}
                                    />
                                )
                            }
                        >
                            {section === 'details' && <ProjectDetailsFields form={form} readOnly={!canManage} />}
                            {section === 'knowledge' && (
                                <ProjectKnowledgeFields
                                    form={form}
                                    project={project}
                                    readOnly={!canEditKnowledge}
                                    canEditUrls={canManage}
                                    canConfigureSources={canConfigureSources}
                                    canDeleteFile={(file) =>
                                        file instanceof File || project.canManage || file.createdBy?.id === user?.id
                                    }
                                    renderFileDetails={(file: any) =>
                                        file.createdBy
                                            ? t('projects.knowledge.by', {
                                                  name:
                                                      file.createdBy.id === user?.id
                                                          ? t('projects.you')
                                                          : displayName(file.createdBy),
                                              })
                                            : null
                                    }
                                />
                            )}
                            {section === 'knowledge' && <ProjectInstructionsFields form={form} readOnly={!canManage} />}
                            {section === 'tools' && (
                                <ProjectToolsFields
                                    form={form}
                                    readOnly={!canManage}
                                    canConfigureProviders={canConfigureSources}
                                />
                            )}
                            {section === 'sharing' && (
                                <ProjectSharingFields
                                    value={recipients}
                                    onValueChange={(ids) => form.setValue('recipients', ids, { shouldDirty: true })}
                                    isReadOnly={!canManage}
                                    shared={project}
                                />
                            )}
                        </SettingsFormCard>
                    </div>
                </Page.Content>
            </div>
            {deleting && (
                <DeleteProjectConfirm
                    project={project}
                    fileCount={knowledgeFileCount(project)}
                    isDisabled={deleteLoading}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleting(false)}
                />
            )}
        </div>
    );
};

/** Projects open as a page in place of the thread (the conversation sidebar stays); creation is a modal on top. */
export const ProjectsPage = ({
    projectId,
    section,
    onOpenSidebar,
}: {
    projectId?: string;
    section?: string;
    onOpenSidebar: () => void;
}) =>
    projectId ? (
        <ProjectDetail key={projectId} projectId={projectId} sectionParam={section} onOpenSidebar={onOpenSidebar} />
    ) : (
        <ProjectList onOpenSidebar={onOpenSidebar} />
    );
